/**
 * Google Play Billing wrapper.
 *
 * cordova-plugin-purchase is the de-facto cross-platform IAP library; it works
 * with Capacitor projects (Capacitor includes the Cordova bridge by default).
 *
 * Web: skipped — `Store.jsx` continues to use Stripe.
 * Android: registers consumable products with the Play Store, lets the user
 *           pay, and POSTs the purchase token to /api/play-billing/verify.
 *
 * On purchase success the backend will idempotently credit gems + chips.
 */
import axios from "axios";
import { API, getOrCreateUserId } from "./api";
import { isNative } from "./native";

let _store = null;
let _ready = false;
let _readyResolvers = [];

export async function initPlayBilling() {
  if (_ready) return _store;
  if (!isNative()) return null;

  try {
    // cordova-plugin-purchase exposes `CdvPurchase` on window.
    const mod = await import("cordova-plugin-purchase");
    // Some bundlers wrap the export
    const CdvPurchase = mod.CdvPurchase || (window).CdvPurchase || mod.default?.CdvPurchase;
    if (!CdvPurchase) {
      console.warn("CdvPurchase not exposed; Play Billing disabled");
      return null;
    }
    _store = CdvPurchase.store;

    // Fetch the catalog from our backend so we know which SKUs to register
    const products = await fetchPlayProducts();

    products.forEach((p) => {
      _store.register({
        id: p.id,
        type: CdvPurchase.ProductType.CONSUMABLE,
        platform: CdvPurchase.Platform.GOOGLE_PLAY,
      });
    });

    _store.when()
      .approved(async (transaction) => {
        try {
          const product = transaction.products?.[0] || {};
          const product_id = product.id;
          const purchase_token = transaction.transactionId;
          const order_id = transaction.purchaseId;
          if (!product_id || !purchase_token) {
            console.warn("approved transaction missing fields", transaction);
            return;
          }
          // Hand to backend for verification + crediting
          const user_id = getOrCreateUserId();
          await axios.post(`${API}/play-billing/verify`, {
            user_id, product_id, purchase_token, order_id,
          });
          // Now consume so the user can buy again
          transaction.finish();
        } catch (e) {
          console.error("Backend verify failed:", e);
          // DO NOT finish() — Play will retry on next launch
        }
      })
      .verified((receipt) => receipt.finish());

    await _store.initialize([CdvPurchase.Platform.GOOGLE_PLAY]);
    _ready = true;
    _readyResolvers.forEach((r) => r());
    _readyResolvers = [];
    return _store;
  } catch (e) {
    console.error("Play Billing init failed:", e);
    return null;
  }
}

export function whenPlayBillingReady(timeoutMs = 6000) {
  if (_ready) return Promise.resolve();
  if (!isNative()) return Promise.resolve(); // resolve immediately on web
  // Resolve on ready, OR reject after `timeoutMs` so the UI never hangs
  // (e.g. when no in-app products are configured in Play Console yet).
  return new Promise((resolve, reject) => {
    let done = false;
    _readyResolvers.push(() => {
      if (done) return;
      done = true;
      resolve();
    });
    setTimeout(() => {
      if (done) return;
      done = true;
      reject(new Error("Play Billing initialization timed out"));
    }, timeoutMs);
  });
}

export async function fetchPlayProducts() {
  const res = await axios.get(`${API}/play-billing/products`);
  return res.data.products || [];
}

/** Initiate a purchase via Play Billing. Throws on web. */
export async function buyPlayProduct(product_id) {
  if (!isNative() || !_store) {
    throw new Error("Play Billing only available on Android");
  }
  const product = _store.get(product_id);
  if (!product) {
    throw new Error("PRODUCT_NOT_READY");
  }
  // If the SKU isn't actually available in Play Console (or the build's
  // signing certificate doesn't match the upload key registered with the
  // SKUs), getOffer() returns undefined OR throws deep inside the plugin
  // with "Cannot read property 'google' of undefined". Detect both shapes
  // and surface a friendly error the UI can map to the "Coming soon" copy.
  let offer = null;
  try { offer = product.getOffer(); } catch { offer = null; }
  if (!offer) {
    throw new Error("PRODUCT_NOT_READY");
  }
  try {
    await offer.order();
  } catch (e) {
    const msg = e?.message || String(e);
    if (msg.includes("'google'") || msg.includes("undefined")) {
      throw new Error("PRODUCT_NOT_READY");
    }
    throw e;
  }
}
